import {
	AddRounded,
	CancelRounded,
	DeleteRounded,
	EditRounded,
	SaveRounded,
} from "@mui/icons-material";
import {
	Box,
	Button,
	Checkbox,
	FormControl,
	FormHelperText,
	IconButton,
	LinearProgress,
	MenuItem,
	Select,
	Stack,
	Typography,
} from "@mui/material";
import { blue, grey } from "@mui/material/colors";
import { DataGrid } from "@mui/x-data-grid";
import axios from "axios";
import { GlobalState } from "context/GlobalContext";
import { PimDashboardState } from "context/PimDashboardContext";
import { useEffect, useMemo, useState } from "react";
import { attrGroupLinkedTables, styleColorAssocObj } from "components/PimDashboard/data";
import { inputTypes } from "data/globalData";
import { parseErrorMessage } from "utils/ErrorFunctions/parseErrorMessage";
import {
	normalizeTrimBreakdownSizeData,
	sectionCompletedStatus,
	sectionDetailsColumns,
	valueAddConsumptionAssociationObj,
	valueAddConsumptionPayloadKey,
	valueAddDetailsCompleteApi,
	valueAddTableColumns,
	getValueAddCompleteValidationError,
	reconcileValueAddBreakdownRows,
	getValueAddItemConsumptionRows,
	buildBreakdownPayloadRows,
	isTruthyBreakdownLevel,
} from "../data";
import { RightWrongBox } from "../TrimsSection/TrimsSection";
import SectionCompleteButton from "../SectionCompleteButton";
import ValueAddBreakdown from "./ValueAddBreakdown/ValueAddBreakdown";

const colStyle = {
	headerAlign: "center",
	align: "center",
};

const ValueAddSection = ({
	styleId,
	styleValueAddArr = [],
	sectionDetails,
	onSectionDetailsUpdate = () => {},
	updatedValueAddArr = [],
	setUpdatedValueAddArr = () => {},
	setStyleValueAddArr = () => {},
	publishDisabled,
}) => {
	const { selectedStyle, allAttributes, commodityCatMaster, setCommodityCatMaster, placementMaster, setPlacementMaster } =
		PimDashboardState();
	const { setSnackBar, setSnackBarOpen, snackBar, setLoadingOpen } = GlobalState();
	const [isEditMode, setIsEditMode] = useState(false);
	const [valueAddArr, setValueAddArr] = useState(styleValueAddArr?.length ? styleValueAddArr : []);
	const [valueAddToAdd, setValueAddToAdd] = useState(() =>
		Object.keys(valueAddTableColumns).reduce((acc, row) => {
			acc[valueAddTableColumns[row].key] = valueAddTableColumns[row]?.defaultValue;
			return acc;
		}, {}),
	);
	const [selectedCategoryDesc, setSelectedCategoryDesc] = useState();
	const [descriptionLoad, setDescriptionLoad] = useState(false);
	const [breakdownOpen, setBreakdownOpen] = useState(false);
	const [selectedValueAddItem, setSelectedValueAddItem] = useState();

	const categoryKey = valueAddTableColumns.category.key;
	const descriptionKey = valueAddTableColumns.description.key;
	const colorLevelKey = valueAddTableColumns.color_level.key;
	const sizeLevelKey = valueAddTableColumns.size_level.key;
	const channelLevelKey = valueAddTableColumns.channel_level.key;

	const sizeAttrCol = useMemo(() => allAttributes?.find((row) => row?.key === "sizes"), [allAttributes]);
	const selectedStyleColors = useMemo(
		() => selectedStyle?.[attrGroupLinkedTables.color.key] ?? selectedStyle?.[styleColorAssocObj] ?? [],
		[selectedStyle],
	);
	const selectedStyleSizes = useMemo(() => {
		const sizeData =
			(sizeAttrCol?.linked_table && selectedStyle?.[sizeAttrCol.linked_table]?.[sizeAttrCol.key]) ??
			selectedStyle?.[sizeAttrCol?.linked_table]?.sizes ??
			[];
		return normalizeTrimBreakdownSizeData(sizeData);
	}, [selectedStyle, sizeAttrCol]);

	const categoryMaster = useMemo(
		() => commodityCatMaster?.filter((row) => row?.commodity_type === "V") ?? [],
		[commodityCatMaster],
	);

	const placementOptions = useMemo(
		() => [...new Set((placementMaster ?? []).map((row) => row?.name).filter(Boolean))],
		[placementMaster],
	);

	useEffect(() => {
		setValueAddArr(styleValueAddArr?.length ? styleValueAddArr : []);
	}, [styleValueAddArr]);

	useEffect(() => {
		const fetchMasters = async () => {
			try {
				if (!commodityCatMaster?.length) {
					const res = await axios.get("/api/master/get_commodity_cat_master?categories=V");
					setCommodityCatMaster(res.data?.result);
				}
				if (!placementMaster?.length) {
					const res = await axios.get("/api/master/get_value_add_placement_master");
					setPlacementMaster(res.data);
				}
			} catch (error) {
				console.log(error);
			}
		};
		fetchMasters();
	}, []);

	const handleCancelClick = () => {
		setValueAddArr(styleValueAddArr?.length ? styleValueAddArr : []);
		setUpdatedValueAddArr([]);
		setValueAddToAdd(
			Object.keys(valueAddTableColumns).reduce((acc, row) => {
				acc[valueAddTableColumns[row].key] = valueAddTableColumns[row]?.defaultValue;
				return acc;
			}, {}),
		);
		setSelectedCategoryDesc();
		setSelectedValueAddItem();
		setBreakdownOpen(false);
		setIsEditMode(false);
	};

	const buildValueAddPayloadRow = (row) => {
		if (row?.is_deleted) return { id: row.id, is_deleted: true };
		const isNew = row?.is_new === true;
		const hasBreakdown =
			isTruthyBreakdownLevel(row?.[colorLevelKey]) ||
			isTruthyBreakdownLevel(row?.[sizeLevelKey]) ||
			isTruthyBreakdownLevel(row?.[channelLevelKey]);
		const consumptionRows = hasBreakdown
			? buildBreakdownPayloadRows(row?.[valueAddConsumptionAssociationObj] ?? [], {
					isNewParent: isNew,
					breakdownOnly: row?.is_breakdown_only === true,
				})
			: undefined;

		if (row?.is_breakdown_only) {
			return { id: row.id, is_new: false, [valueAddConsumptionPayloadKey]: consumptionRows ?? [] };
		}

		const { id, is_new, is_breakdown_only, [valueAddConsumptionAssociationObj]: _, ...rest } = row;
		const valueAddRow = { is_new: isNew, ...rest };
		if (!isNew) valueAddRow.id = id;
		if (consumptionRows) valueAddRow[valueAddConsumptionPayloadKey] = consumptionRows;
		return valueAddRow;
	};

	const handleSaveClick = async () => {
		if (!updatedValueAddArr?.length) {
			setSnackBarOpen(true);
			setSnackBar({
				...snackBar,
				message: "No Changes To Save",
				severity: "warning",
				anchorOrigin: { horizontal: "center", vertical: "top" },
			});
			return;
		}

		setLoadingOpen(true);
		try {
			const payload = { valueAddArr: updatedValueAddArr.map(buildValueAddPayloadRow) };
			const res = await axios.post(`/api/fabric_trims/save_value_addition_data?styleId=${styleId}`, payload);
			const created = (res?.data?.result?.createdValueAddData ?? []).map((row) => ({
				...row,
				[valueAddConsumptionAssociationObj]:
					row?.[valueAddConsumptionAssociationObj] ?? row?.valueAddConsumption ?? [],
			}));
			const updated = (res?.data?.result?.updateValueAddData ?? []).map((row) => ({
				...row,
				[valueAddConsumptionAssociationObj]:
					row?.[valueAddConsumptionAssociationObj] ?? row?.valueAddConsumption ?? [],
			}));
			const savedIds = [...created, ...updated].map((r) => r?.id).filter(Boolean);
			const savedValueAddArr = [
				...valueAddArr
					.filter((row) => !row?.is_new && !savedIds.includes(row?.id))
					.map(({ is_breakdown_only, ...row }) => row),
				...updated,
				...created,
			];
			setStyleValueAddArr(savedValueAddArr);
			setUpdatedValueAddArr([]);
			setIsEditMode(false);
			setSnackBarOpen(true);
			setSnackBar({
				...snackBar,
				message: "Value add saved successfully",
				severity: "success",
				anchorOrigin: { horizontal: "center", vertical: "top" },
			});
		} catch (error) {
			setSnackBarOpen(true);
			setSnackBar({
				...snackBar,
				message: parseErrorMessage(error),
				severity: "error",
				anchorOrigin: { horizontal: "center", vertical: "top" },
			});
		} finally {
			setLoadingOpen(false);
		}
	};

	const isSectionCompleted = sectionDetails?.[sectionDetailsColumns.is_completed.key] === sectionCompletedStatus;

	const handleCompleteClick = async () => {
		const validationError = getValueAddCompleteValidationError(valueAddArr);
		if (validationError) {
			setSnackBarOpen(true);
			setSnackBar({
				...snackBar,
				message: validationError,
				severity: "warning",
				anchorOrigin: { horizontal: "center", vertical: "top" },
			});
			return;
		}

		setLoadingOpen(true);
		try {
			const res = await axios.post(`${valueAddDetailsCompleteApi}?styleId=${styleId}`);
			const updatedDetails = res?.data?.result;
			onSectionDetailsUpdate(updatedDetails ?? {});
			setSnackBarOpen(true);
			setSnackBar({
				...snackBar,
				message: "Value add section marked complete",
				severity: "success",
				anchorOrigin: { horizontal: "center", vertical: "top" },
			});
		} catch (error) {
			setSnackBarOpen(true);
			setSnackBar({
				...snackBar,
				message: parseErrorMessage(error),
				severity: "error",
				anchorOrigin: { horizontal: "center", vertical: "top" },
			});
		} finally {
			setLoadingOpen(false);
		}
	};

	const handleAddValueAddItem = () => {
		const currentItem = valueAddToAdd;
		if (!currentItem || currentItem[categoryKey] === inputTypes.SELECT.key || currentItem[descriptionKey] === inputTypes.SELECT.key) {
			setSnackBarOpen(true);
			setSnackBar({
				...snackBar,
				message: "Select Details",
				severity: "error",
				anchorOrigin: { horizontal: "center", vertical: "top" },
			});
			return;
		}
		if (currentItem?.[colorLevelKey] === true && !selectedStyleColors.length) {
			setSnackBarOpen(true);
			setSnackBar({
				...snackBar,
				message: "No colors found in selected style",
				severity: "error",
				anchorOrigin: { horizontal: "center", vertical: "top" },
			});
			return;
		}
		if (currentItem?.[sizeLevelKey] === true && !selectedStyleSizes.length) {
			setSnackBarOpen(true);
			setSnackBar({
				...snackBar,
				message: "No sizes found in selected style",
				severity: "error",
				anchorOrigin: { horizontal: "center", vertical: "top" },
			});
			return;
		}

		const objToAdd = {
			...currentItem,
			is_new: true,
			id: new Date().valueOf(),
		};
		objToAdd[valueAddConsumptionAssociationObj] = reconcileValueAddBreakdownRows({
			valueAddObj: objToAdd,
			colorRows: selectedStyleColors,
			sizeRows: selectedStyleSizes,
			existingRows: [],
		});
		setValueAddArr((prev) => [...prev, objToAdd]);
		setUpdatedValueAddArr((prev) => [...prev, objToAdd]);
		setValueAddToAdd(
			Object.keys(valueAddTableColumns).reduce((acc, row) => {
				acc[valueAddTableColumns[row].key] = valueAddTableColumns[row]?.defaultValue;
				return acc;
			}, {}),
		);
	};

	const handleCategorySelectChange = async (e) => {
		const categoryValue = e.target.value;
		setDescriptionLoad(true);
		try {
			const res = await axios.get(`/api/master/get_commodity_cat_desc_master?category=${categoryValue}`);
			setSelectedCategoryDesc(res.data ?? []);
		} catch (error) {
			setSelectedCategoryDesc([]);
		} finally {
			setDescriptionLoad(false);
		}
		setValueAddToAdd((prev) => ({
			...prev,
			[categoryKey]: categoryValue,
			[descriptionKey]: inputTypes.SELECT.key,
		}));
	};

	const openBreakdown = (row) => {
		const reconciledBreakdownRows = reconcileValueAddBreakdownRows({
			valueAddObj: row,
			colorRows: selectedStyleColors,
			sizeRows: selectedStyleSizes,
			existingRows: getValueAddItemConsumptionRows(row),
		});
		const itemWithBreakdown = {
			...row,
			[valueAddConsumptionAssociationObj]: reconciledBreakdownRows,
		};
		const existingCount = getValueAddItemConsumptionRows(row).length;
		if (reconciledBreakdownRows.length !== existingCount) {
			setValueAddArr((prev) => prev.map((item) => (item?.id === itemWithBreakdown?.id ? itemWithBreakdown : item)));
			setUpdatedValueAddArr((prev) => {
				const existing = prev.find((item) => item?.id === itemWithBreakdown?.id);
				if (existing) {
					return [
						...prev.filter((item) => item?.id !== itemWithBreakdown?.id),
						{ ...itemWithBreakdown, is_breakdown_only: existing?.is_breakdown_only },
					];
				}
				if (itemWithBreakdown?.is_new) {
					return [...prev, itemWithBreakdown];
				}
				return [...prev, { ...itemWithBreakdown, is_breakdown_only: true }];
			});
		}
		setSelectedValueAddItem(itemWithBreakdown);
		setBreakdownOpen(true);
	};

	const handleBreakdownClose = () => setBreakdownOpen(false);

	const handleValueAddTableRowUpdate = (updated) => {
		setValueAddArr((prev) => prev.map((row) => (row?.id === updated?.id ? updated : row)));
		setUpdatedValueAddArr((prev) => prev.map((row) => (row?.id === updated?.id ? { ...updated, is_breakdown_only: false } : row)));
		return updated;
	};

	const handleBreakdownValueAddUpdate = (updatedItem) => {
		setValueAddArr((prev) => prev.map((row) => (row?.id === updatedItem?.id ? updatedItem : row)));
		setUpdatedValueAddArr((prev) => {
			const existing = prev.find((row) => row?.id === updatedItem?.id);
			const isBreakdownOnly = !updatedItem?.is_new && !existing?.is_new && existing?.is_breakdown_only !== false;
			return [...prev.filter((row) => row?.id !== updatedItem?.id), { ...updatedItem, is_breakdown_only: isBreakdownOnly }];
		});
		setSelectedValueAddItem(updatedItem);
		return updatedItem;
	};

	const handleDeleteValueAddClick = (row) => {
		if (row?.is_new) {
			setValueAddArr((prev) => prev.filter((r) => r?.id !== row.id));
			setUpdatedValueAddArr((prev) => prev.filter((r) => r?.id !== row.id));
		} else {
			setValueAddArr((prev) => prev.filter((r) => r?.id !== row.id));
			setUpdatedValueAddArr((prev) => [...prev.filter((r) => r?.id !== row.id), { ...row, is_deleted: true }]);
		}
	};

	const defValueAddColumns = [
		{
			...valueAddTableColumns.category,
			flex: 1.2,
			...colStyle,
			headerName: valueAddTableColumns.category.name,
			field: categoryKey,
			renderCell: ({ field, row }) => (
				<Box component="div" onClick={() => openBreakdown(row)}>
					<span style={{ color: blue[600] }}>{row[field]}</span>
				</Box>
			),
		},
		{
			...valueAddTableColumns.description,
			field: descriptionKey,
			headerName: valueAddTableColumns.description.name,
			flex: 1.4,
			...colStyle,
		},
		{
			...valueAddTableColumns.color_level,
			flex: 0.8,
			...colStyle,
			headerName: valueAddTableColumns.color_level.name,
			field: colorLevelKey,
			renderCell: ({ field, row }) => <RightWrongBox field={field} row={row} />,
		},
		{
			...valueAddTableColumns.size_level,
			headerName: valueAddTableColumns.size_level.name,
			field: sizeLevelKey,
			flex: 0.8,
			...colStyle,
			renderCell: ({ field, row }) => <RightWrongBox field={field} row={row} />,
		},
		{
			...valueAddTableColumns.channel_level,
			headerName: valueAddTableColumns.channel_level.name,
			field: channelLevelKey,
			flex: 0.8,
			...colStyle,
			renderCell: ({ field, row }) => <RightWrongBox field={field} row={row} />,
		},
		{
			headerName: "Delete",
			flex: 0.8,
			...colStyle,
			renderCell: ({ row }) => (
				<IconButton onClick={() => handleDeleteValueAddClick(row)} disabled={publishDisabled || !isEditMode}>
					<DeleteRounded />
				</IconButton>
			),
		},
	];

	return (
		<Box
			sx={{
				border: `1px solid ${grey[300]}`,
				borderRadius: 2,
				overflow: "hidden",
				backgroundColor: "#fff",
				width: "100%",
				minWidth: 0,
				boxSizing: "border-box",
			}}>
			<Box
				display="flex"
				justifyContent="space-between"
				alignItems="flex-start"
				rowGap={1.5}
				flexWrap="wrap"
				px={2}
				py={1.5}
				sx={{
					backgroundColor: grey[50],
					borderBottom: `1px solid ${grey[300]}`,
				}}>
				<Stack spacing={1.25} flex={1} minWidth={320}>
					<Box display="flex" alignItems="center" columnGap={1.25} flexWrap="wrap" rowGap={0.75}>
						<Typography variant="h6" sx={{ fontWeight: 700, color: grey[900] }}>
							Value Addition
						</Typography>
						{isSectionCompleted && (
							<Typography variant="caption" sx={{ color: grey[600] }}>
								Completed by {sectionDetails?.[sectionDetailsColumns.completed_by.key] || "—"}
							</Typography>
						)}
					</Box>
					{isEditMode && (
						<Box
							sx={{
								display: "flex",
								flexWrap: "wrap",
								alignItems: "center",
								columnGap: 1,
								rowGap: 1,
							}}>
							<FormControl>
								<Select
									sx={{ width: 170, backgroundColor: "#fff", borderRadius: 1 }}
									size="small"
									displayEmpty
									value={valueAddToAdd?.[categoryKey] ?? inputTypes.SELECT.key}
									onChange={handleCategorySelectChange}
									MenuProps={{ PaperProps: { sx: { maxHeight: 200 } } }}>
									<MenuItem value={inputTypes.SELECT.key}>
										<span style={{ color: grey[400] }}>Select Category</span>
									</MenuItem>
									{categoryMaster?.map((row) => (
										<MenuItem key={row?.category} value={row?.category}>
											{row?.category}
										</MenuItem>
									))}
								</Select>
								<FormHelperText>
									<LinearProgress sx={{ display: descriptionLoad ? "block" : "none" }} />
								</FormHelperText>
							</FormControl>

							<Select
								sx={{ width: 170, backgroundColor: "#fff", borderRadius: 1 }}
								size="small"
								displayEmpty
								value={valueAddToAdd?.[descriptionKey] ?? inputTypes.SELECT.key}
								onChange={(e) => {
									const selectedDesc = selectedCategoryDesc?.find((row) => row?.description === e.target.value);
									if (selectedDesc) {
										setValueAddToAdd((prev) => ({
											...prev,
											[descriptionKey]: selectedDesc.description,
										}));
									}
								}}
								MenuProps={{ PaperProps: { sx: { maxHeight: 200 } } }}>
								<MenuItem value={inputTypes.SELECT.key}>
									<span style={{ color: grey[400] }}>Select Description</span>
								</MenuItem>
								{selectedCategoryDesc?.map((row) => (
									<MenuItem key={row?.description} value={row?.description}>
										{row?.description}
									</MenuItem>
								))}
							</Select>

							<Box display="flex" alignItems="center">
								<Checkbox
									checked={valueAddToAdd?.[colorLevelKey] === true}
									onChange={(e) => setValueAddToAdd((prev) => ({ ...prev, [colorLevelKey]: e.target.checked }))}
								/>
								<Typography variant="subtitle1">{valueAddTableColumns.color_level.name}</Typography>
							</Box>
							<Box display="flex" alignItems="center">
								<Checkbox
									checked={valueAddToAdd?.[sizeLevelKey] === true}
									onChange={(e) => setValueAddToAdd((prev) => ({ ...prev, [sizeLevelKey]: e.target.checked }))}
								/>
								<Typography variant="subtitle1">{valueAddTableColumns.size_level.name}</Typography>
							</Box>
							<Box display="flex" alignItems="center">
								<Checkbox
									checked={valueAddToAdd?.[channelLevelKey] === true}
									onChange={(e) => setValueAddToAdd((prev) => ({ ...prev, [channelLevelKey]: e.target.checked }))}
								/>
								<Typography variant="subtitle1">{valueAddTableColumns.channel_level.name}</Typography>
							</Box>
							<Button size="small" variant="contained" onClick={handleAddValueAddItem} startIcon={<AddRounded />}>
								Add Value Add
							</Button>
						</Box>
					)}
				</Stack>
				<Box display="flex" columnGap={1} alignItems="center" flexWrap="wrap" rowGap={1}>
					{isEditMode ? (
						<>
							<Button size="small" variant="outlined" onClick={handleCancelClick} startIcon={<CancelRounded />}>
								Cancel
							</Button>
							<Button size="small" variant="contained" onClick={handleSaveClick} startIcon={<SaveRounded />}>
								Save
							</Button>
						</>
					) : (
						<>
							<SectionCompleteButton sectionDetails={sectionDetails} onComplete={handleCompleteClick} />
							<Button
								size="small"
								variant="contained"
								onClick={() => setIsEditMode(true)}
								startIcon={<EditRounded />}
								sx={{ textTransform: "none", fontWeight: 700 }}>
								Edit
							</Button>
						</>
					)}
				</Box>
			</Box>

			<Box width="100%" sx={{ overflow: "hidden", boxSizing: "border-box" }}>
				<DataGrid
					columns={defValueAddColumns}
					rows={valueAddArr}
					getRowId={(row) => row?.id}
					sx={{
						width: "100%",
						maxWidth: "100%",
						height: 170,
						background: "#fff",
						border: "none",
						"& .MuiDataGrid-row": {
							cursor: "pointer",
							backgroundColor: "#fff",
							"&:nth-of-type(even)": { backgroundColor: grey[50] },
						},
						"& .MuiDataGrid-columnHeader": {
							backgroundColor: grey[100],
							borderBottom: `1px solid ${grey[300]}`,
						},
						"& .MuiDataGrid-cell": { padding: 0, borderBottom: `1px solid ${grey[200]}` },
						"& .MuiDataGrid-columnHeaders": { background: grey[100], borderRadius: 0 },
					}}
					columnHeaderHeight={35}
					rowHeight={35}
					showCellVerticalBorder
					hideFooter
					disableRowSelectionOnClick
					processRowUpdate={handleValueAddTableRowUpdate}
				/>
			</Box>

			<ValueAddBreakdown
				open={breakdownOpen}
				onClose={handleBreakdownClose}
				selectedValueAddItem={valueAddArr.find((item) => item?.id === selectedValueAddItem?.id) ?? selectedValueAddItem}
				onUpdateValueAddItem={handleBreakdownValueAddUpdate}
				isEditMode={isEditMode}
				placementOptions={placementOptions}
			/>
		</Box>
	);
};

export default ValueAddSection;
